import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    CircularProgress,
    Avatar,
    Paper,
    Tabs,
    Tab,
    Dialog,
    IconButton,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    GridOn as GridIcon,
    Favorite as FavoriteIcon,
    Comment as CommentIcon,
    PlayArrow as PlayArrowIcon,
    Close as CloseIcon,
    ArrowBack as ArrowBackIcon,
    PushPin as PushPinIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import contentService from '../services/contentService';
import ContentCard from '../components/ContentCard';
import { useAuth } from '../context/AuthContext';
import { shareContentLink } from '../utils/shareUtils';
import AdminDashboard from '../components/AdminDashboard';
import CustomerDashboard from '../components/CustomerDashboard';
import '../styles/ProtectedRoutes.css';

const UserProfile = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [selectedPost, setSelectedPost] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [uploaderInfo, setUploaderInfo] = useState(null);
    const { user } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleBack = () => {
        navigate(-1);
    };

    const loadPosts = async () => {
        try {
            setLoading(true);
            const response = await contentService.getContentFeed(100, 0);
            
            console.log('=== UserProfile loadPosts DEBUG ===');
            console.log('1. Raw response from API:', response);
            
            if (response.success) {
                console.log('2. All posts from API:', response.data);
                
                // Filter posts by uploader
                const userPosts = response.data.filter(post => post.UploadedBy === parseInt(userId));
                console.log('3. Filtered posts for userId', userId, ':', userPosts);
                console.log('4. Posts BEFORE sorting:', userPosts.map(p => ({ 
                    ContentId: p.ContentId, 
                    IsPinned: p.IsPinned, 
                    CreatedAt: p.CreatedAt 
                })));
                
                // Sort posts: pinned first, then by creation date
                const sortedPosts = userPosts.sort((a, b) => {
                    // If both have same pin status, sort by date
                    if (a.IsPinned === b.IsPinned) {
                        return new Date(b.CreatedAt) - new Date(a.CreatedAt);
                    }
                    // Otherwise, pinned posts come first
                    return (b.IsPinned || 0) - (a.IsPinned || 0);
                });
                
                console.log('5. Posts AFTER sorting:', sortedPosts.map(p => ({ 
                    ContentId: p.ContentId, 
                    IsPinned: p.IsPinned, 
                    CreatedAt: p.CreatedAt 
                })));
                
                setPosts(sortedPosts);
                console.log('6. Posts set to state');
                
                // Get uploader info from first post
                if (sortedPosts.length > 0) {
                    setUploaderInfo({
                        name: sortedPosts[0].UploaderName || 'User',
                        userId: parseInt(userId)
                    });
                }
            } else {
                toast.error('Failed to load posts');
            }
        } catch (error) {
            console.error('ERROR in loadPosts:', error);
            toast.error(error.message || 'Failed to load posts');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPosts();
    }, [userId]);

    const handleTabChange = (_event, newValue) => {
        setTabValue(newValue);
    };

    const handlePostClick = (post) => {
        setSelectedPost(post);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedPost(null);
    };

    const handleUploaderClick = (uploaderId) => {
        // Navigate to uploader's profile
        navigate(`/profile/${uploaderId}`);
    };

    const handleLike = async (contentId) => {
        if (!user || user.role !== 'Customer') {
            toast.error('Please login as a customer to like posts');
            return;
        }

        try {
            const response = await contentService.toggleLike(contentId);
            if (response.success) {
                // Update the posts array with new like status
                setPosts(prev => prev.map(item => 
                    item.ContentId === contentId 
                        ? { 
                            ...item, 
                            LikeCount: response.data.likeCount,
                            IsLikedByUser: response.data.isLiked ? 1 : 0
                        }
                        : item
                ));

                // Also update selected post if it's open
                if (selectedPost && selectedPost.ContentId === contentId) {
                    setSelectedPost(prev => ({
                        ...prev,
                        LikeCount: response.data.likeCount,
                        IsLikedByUser: response.data.isLiked ? 1 : 0
                    }));
                }
            }
        } catch (error) {
            toast.error(error.message || 'Failed to like post');
        }
    };

    const handleComment = async (contentId, commentText) => {
        if (!user) {
            toast.error('Please login to comment');
            return;
        }

        try {
            const response = await contentService.addComment(contentId, commentText);
            if (response.success) {
                // Update comment count
                setPosts(prev => prev.map(item => 
                    item.ContentId === contentId 
                        ? { ...item, CommentCount: parseInt(item.CommentCount) + 1 }
                        : item
                ));

                // Also update selected post if it's open
                if (selectedPost && selectedPost.ContentId === contentId) {
                    setSelectedPost(prev => ({
                        ...prev,
                        CommentCount: parseInt(prev.CommentCount) + 1
                    }));
                }

                toast.success('Comment added successfully');
                return true;
            }
        } catch (error) {
            toast.error(error.message || 'Failed to add comment');
            return false;
        }
    };

    const handleShare = async (contentId) => {
        if (!user || user.role !== 'Customer') {
            toast.error('Please login as a customer to share posts');
            return;
        }

        try {
            const response = await contentService.shareContent(contentId, 'link');
            if (response.success) {
                const shareResult = await shareContentLink(contentId, {
                    title: 'Check this post',
                    text: 'Take a look at this post on Shipway'
                });

                setPosts(prev => prev.map(item => 
                    item.ContentId === contentId 
                        ? { ...item, ShareCount: response.data.shareCount }
                        : item
                ));

                if (selectedPost && selectedPost.ContentId === contentId) {
                    setSelectedPost(prev => ({
                        ...prev,
                        ShareCount: response.data.shareCount
                    }));
                }
            }
        } catch (error) {
            toast.error(error.message || 'Failed to share post');
        }
    };

    const handleDelete = (contentId) => {
        // Remove the deleted post from the list
        setPosts(prevPosts => prevPosts.filter(post => post.ContentId !== contentId));
        
        // Close modal if the deleted post was open
        if (selectedPost && selectedPost.ContentId === contentId) {
            handleCloseModal();
        }
    };

    const getMediaUrl = (filePath) => {
        if (!filePath) return '';
        if (filePath.startsWith('http')) return filePath;
        const backendUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
        return `${backendUrl}${filePath}`;
    };

    const stats = {
        posts: posts.length,
        likes: posts.reduce((sum, post) => sum + (parseInt(post.LikeCount) || 0), 0),
        comments: posts.reduce((sum, post) => sum + (parseInt(post.CommentCount) || 0), 0)
    };

    if (loading) {
        const loadingContent = (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} className="glass-content-container">
                    <CircularProgress size={40} className="glass-loading" />
                    <Typography variant="body1" className="glass-text-secondary">
                        Loading profile...
                    </Typography>
                </Box>
            </Container>
        );

        if (user?.role === 'Admin') {
            return <AdminDashboard>{loadingContent}</AdminDashboard>;
        } else if (user?.role === 'Customer') {
            return <CustomerDashboard>{loadingContent}</CustomerDashboard>;
        }
        return loadingContent;
    }

    if (!uploaderInfo) {
        const notFoundContent = (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Paper elevation={1} className="glass-empty-state">
                    <Typography variant="h6" className="glass-text-secondary" gutterBottom>
                        User not found
                    </Typography>
                    <Typography variant="body2" className="glass-text-secondary">
                        This user hasn't posted any content yet.
                    </Typography>
                </Paper>
            </Container>
        );

        if (user?.role === 'Admin') {
            return <AdminDashboard>{notFoundContent}</AdminDashboard>;
        } else if (user?.role === 'Customer') {
            return <CustomerDashboard>{notFoundContent}</CustomerDashboard>;
        }
        return notFoundContent;
    }

    const profileContent = (
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 } }}>
            {/* Back Button */}
            <Box sx={{ mb: 2 }}>
                <IconButton 
                    onClick={handleBack}
                    className="glass-icon-button"
                    sx={{ 
                        color: '#bc965e',
                        '&:hover': {
                            backgroundColor: 'rgba(188, 150, 94, 0.1)'
                        }
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
            </Box>

            {/* Profile Header */}
            <Paper elevation={2} className="glass-card" sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'center', sm: 'flex-start' },
                    gap: { xs: 2, sm: 3 }, 
                    mb: { xs: 2, sm: 3 } 
                }}>
                    <Avatar 
                        className="glass-avatar" 
                        sx={{ 
                            width: { xs: 80, sm: 100, md: 120 }, 
                            height: { xs: 80, sm: 100, md: 120 },
                            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                            bgcolor: '#bc965e'
                        }}
                    >
                        {uploaderInfo.name.charAt(0).toUpperCase()}
                    </Avatar>
                    
                    <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' }, width: '100%' }}>
                        <Typography 
                            variant={isMobile ? 'h6' : 'h5'} 
                            className="glass-text-primary" 
                            gutterBottom
                        >
                            {uploaderInfo.name}
                        </Typography>
                        
                        {/* Stats */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: { xs: 2, sm: 3, md: 4 }, 
                            mt: 2,
                            justifyContent: { xs: 'center', sm: 'flex-start' }
                        }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography 
                                    variant={isMobile ? 'h6' : 'h5'} 
                                    className="glass-text-primary" 
                                    fontWeight="bold"
                                >
                                    {stats.posts}
                                </Typography>
                                <Typography 
                                    variant="caption" 
                                    className="glass-text-secondary"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                >
                                    Posts
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography 
                                    variant={isMobile ? 'h6' : 'h5'} 
                                    className="glass-text-primary" 
                                    fontWeight="bold"
                                >
                                    {stats.likes}
                                </Typography>
                                <Typography 
                                    variant="caption" 
                                    className="glass-text-secondary"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                >
                                    Likes
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography 
                                    variant={isMobile ? 'h6' : 'h5'} 
                                    className="glass-text-primary" 
                                    fontWeight="bold"
                                >
                                    {stats.comments}
                                </Typography>
                                <Typography 
                                    variant="caption" 
                                    className="glass-text-secondary"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                >
                                    Comments
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Paper>

            {/* Tabs */}
            <Paper elevation={1} className="glass-card" sx={{ mb: 2 }}>
                <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange}
                    centered
                    sx={{
                        '& .MuiTab-root': {
                            color: '#bc965e',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            minHeight: { xs: 48, sm: 56 },
                            '&.Mui-selected': {
                                color: '#bc965e'
                            }
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#bc965e'
                        }
                    }}
                >
                    <Tab icon={<GridIcon />} label="POSTS" />
                </Tabs>
            </Paper>

            {/* Posts Grid */}
            {posts.length === 0 ? (
                <Paper elevation={1} className="glass-empty-state">
                    <Typography variant="h6" className="glass-text-secondary" gutterBottom>
                        No posts yet
                    </Typography>
                    <Typography variant="body2" className="glass-text-secondary">
                        This user hasn't posted any content yet.
                    </Typography>
                </Paper>
            ) : (
                <Box sx={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 0.5
                }}>
                    {posts.map((post) => {
                        const isVideo = post.ContentType === 'video';
                        const isPinned = post.IsPinned === 1;
                        const mediaUrl = getMediaUrl(post.FilePath);
                            
                        return (
                        <Box
                            key={post.ContentId}
                            onClick={() => handlePostClick(post)}
                            sx={{ 
                                position: 'relative',
                                paddingTop: '100%',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                backgroundColor: '#1a1a1a',
                                '&:hover .overlay': {
                                    opacity: 1
                                }
                            }}
                        >
                            {isVideo ? (
                                <Box
                                    component="video"
                                    src={mediaUrl}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                            ) : (
                                <Box
                                    component="img"
                                    src={mediaUrl}
                                    alt={post.Title || 'Post'}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                            )}
                                
                                {isVideo && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            zIndex: 2
                                        }}
                                    >
                                        <PlayArrowIcon sx={{ 
                                            color: 'white', 
                                            fontSize: 20,
                                            filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.8))'
                                        }} />
                                    </Box>
                                )}

                                {isPinned && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            left: 8,
                                            zIndex: 2
                                        }}
                                    >
                                        <PushPinIcon sx={{ 
                                            color: '#ffd700', 
                                            fontSize: 20,
                                            filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.8))'
                                        }} />
                                    </Box>
                                )}
                                
                                <Box
                                    className="overlay"
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        display: { xs: 'none', sm: 'flex' },
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 2,
                                        opacity: 0,
                                        transition: 'opacity 0.3s ease',
                                        zIndex: 1
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <FavoriteIcon sx={{ color: 'white', fontSize: 18 }} />
                                        <Typography 
                                            variant="body2" 
                                            sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.875rem' }}
                                        >
                                            {post.LikeCount || 0}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <CommentIcon sx={{ color: 'white', fontSize: 18 }} />
                                        <Typography 
                                            variant="body2" 
                                            sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.875rem' }}
                                        >
                                            {post.CommentCount || 0}
                                        </Typography>
                                    </Box>
                            </Box>
                        </Box>
                    )})}
                </Box>
            )}

            {/* Post Detail Modal */}
            <Dialog
                open={modalOpen}
                onClose={handleCloseModal}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                slotProps={{
                    paper: {
                        sx: {
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            maxHeight: { xs: '100vh', sm: '90vh' },
                            m: { xs: 0, sm: 2 }
                        }
                    }
                }}
            >
                <Box sx={{ position: 'relative', height: '100%' }}>
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{
                            position: 'absolute',
                            top: { xs: 8, sm: 16 },
                            right: { xs: 8, sm: 16 },
                            zIndex: 1300,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.7)'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    {selectedPost && (
                        <Box sx={{ 
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: { xs: 0, sm: 2 }
                        }}>
                            <Box sx={{ 
                                width: '100%',
                                maxWidth: { xs: '100%', sm: '425px' }
                            }}>
                                <ContentCard
                                    content={selectedPost}
                                    onLike={handleLike}
                                    onComment={handleComment}
                                    onShare={handleShare}
                                    currentUser={user}
                                    onUploaderClick={handleUploaderClick}
                                    onDelete={handleDelete}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>
            </Dialog>
        </Container>
    );

    // Render profile content with dashboard wrapper based on user role
    // The dashboard wrappers provide navbar and sidebar
    if (user?.role === 'Admin') {
        return <AdminDashboard>{profileContent}</AdminDashboard>;
    } else if (user?.role === 'Customer') {
        return <CustomerDashboard>{profileContent}</CustomerDashboard>;
    } else {
        // For non-logged-in users or other roles, show without dashboard wrapper
        return profileContent;
    }
};

export default UserProfile;
